(() => {
  if (customElements.get('qeydo-menu-promo')) return;
  const images = new Map();
  function prepareImage(url) {
    if (!url) return Promise.resolve(false);
    if (images.has(url)) return images.get(url);
    const pending = new Promise(resolve => {
      const image = new Image();
      let done = false;
      const finish = ok => {
        if (done) return;
        done = true;
        window.clearTimeout(timeout);
        if (!ok) images.delete(url);
        resolve(ok);
      };
      const timeout = window.setTimeout(() => finish(false), 2500);
      image.fetchPriority = 'low';
      image.onload = () => {
        if (typeof image.decode === 'function') image.decode().catch(() => {}).then(() => finish(true));
        else finish(true);
      };
      image.onerror = () => finish(false);
      image.src = url;
    });
    images.set(url, pending);
    return pending;
  }
  class QeydoMenuPromo extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      try { this.items = JSON.parse(this.querySelector('[data-promo-products]').textContent); }
      catch { return; }
      if (!Array.isArray(this.items) || !this.items.length) return;
      this.index = this.cursor = 0;
      // HTMLElement.title is a native string property, never a reference to a DOM element.
      this.nameEl = this.querySelector('[data-promo-name]');
      this.descriptionEl = this.querySelector('[data-promo-description]');
      this.imageEl = this.querySelector('[data-promo-image]');
      this.linkEl = this.querySelector('[data-promo-link]');
      this.pauseEl = this.querySelector('[data-promo-pause]');
      if (!this.nameEl || !this.descriptionEl || !this.linkEl) return;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.manuallyPaused = false;
      this.opened = false;
      this.busy = false;
      this.updatePause();
      this.pauseEl?.addEventListener('click', () => {
        this.manuallyPaused = !this.manuallyPaused;
        this.updatePause();
      }, options);
      this.menuRoot = this.closest('.header__mega-menu, .menu-drawer__submenu, .menu-drawer') || this.parentElement;
      this.observer = new MutationObserver(() => this.queueVisibility());
      for (let node = this; node && node !== document.body; node = node.parentElement) {
        this.observer.observe(node, {attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-expanded']});
        node.addEventListener('toggle', () => this.queueVisibility(), options);
      }
      const triggerArea = this.menuRoot.parentElement || this.menuRoot;
      for (const event of ['pointerenter', 'pointerleave', 'focusin', 'focusout', 'transitionend']) {
        triggerArea.addEventListener(event, () => this.queueVisibility(), options);
      }
      document.addEventListener('visibilitychange', () => this.queueVisibility(), options);
      window.addEventListener('resize', () => this.queueVisibility(), options);
      if (this.imageEl) {
        this.imageEl.loading = 'eager';
        this.prefetchNext();
      }
      this.queueVisibility();
    }
    disconnectedCallback() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.fadeTimer);
      window.cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.abort?.abort();
      this.generation = (this.generation || 0) + 1;
      this.classList.remove('is-changing');
      this.busy = false;
    }
    queueVisibility() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.syncVisibility());
    }
    isMenuVisible() {
      if (!this.isConnected || document.hidden || !this.getClientRects().length) return false;
      for (let node = this; node && node !== document.body; node = node.parentElement) {
        if (node.tagName === 'DETAILS' && !node.open) return false;
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      }
      return true;
    }
    syncVisibility() {
      const visible = this.isMenuVisible();
      if (visible === this.opened) return;
      this.opened = visible;
      window.clearInterval(this.timer);
      if (!visible) {
        window.clearTimeout(this.fadeTimer);
        this.generation++;
        this.classList.remove('is-changing');
        this.busy = false;
        return;
      }
      // Opening starts with the cheapest model and a fresh three-second interval.
      this.index = this.cursor = 0;
      this.commit(this.items[0]);
      this.prefetchNext();
      if (this.items.length > 1) this.timer = window.setInterval(() => this.next(), 3000);
    }
    updatePause() {
      if (!this.pauseEl) return;
      this.pauseEl.setAttribute('aria-pressed', String(this.manuallyPaused));
      this.pauseEl.setAttribute('aria-label', this.manuallyPaused ? 'Reprendre le défilement des modèles' : 'Mettre le défilement en pause');
      const symbol = this.pauseEl.querySelector('[data-promo-pause-symbol]');
      if (symbol) symbol.textContent = this.manuallyPaused ? '▷' : 'Ⅱ';
    }
    canRotate() {
      // Keep a link stable while it is about to be activated; hovering the photo does not stop the loop.
      return this.isMenuVisible() && !this.manuallyPaused && !this.linkEl.matches(':hover, :focus');
    }
    prefetchNext() {
      if (this.imageEl && this.items.length > 1) prepareImage(this.items[(this.cursor + 1) % this.items.length].image);
    }
    commit(item) {
      this.nameEl.textContent = item.name;
      this.descriptionEl.textContent = item.description || '';
      this.linkEl.href = item.url;
      this.linkEl.setAttribute('aria-label', `Découvrir ${item.name}`);
      if (this.imageEl && item.image) {
        if (this.imageEl.getAttribute('src') !== item.image) this.imageEl.src = item.image;
        this.imageEl.alt = item.alt || item.name;
        this.imageEl.hidden = false;
      }
    }
    async next() {
      if (this.busy || !this.canRotate()) return;
      this.busy = true;
      const generation = this.generation;
      const index = (this.cursor + 1) % this.items.length;
      const item = this.items[index];
      this.cursor = index;
      try {
        if (this.imageEl && !(await prepareImage(item.image))) return;
        if (generation !== this.generation || !this.canRotate()) return;
        if (!this.motion.matches) this.classList.add('is-changing');
        this.fadeTimer = window.setTimeout(() => {
          try {
            if (generation === this.generation && this.canRotate()) {
              this.commit(item);
              this.index = index;
            }
          } finally {
            if (generation === this.generation) {
              this.classList.remove('is-changing');
              this.busy = false;
              this.prefetchNext();
            }
          }
        }, this.motion.matches ? 0 : 160);
      } catch {
        if (generation === this.generation) this.classList.remove('is-changing');
      } finally {
        if (generation === this.generation) {
          if (!this.classList.contains('is-changing')) this.busy = false;
          this.prefetchNext();
        }
      }
    }
  }
  customElements.define('qeydo-menu-promo', QeydoMenuPromo);
})();
